/*
	- The theme is designed to maintain a 1:1 relationship between observer triggers and vimeo videos.
	- Each observer trigger corresponds to a specific vimeo video, ensuring precise control over video loading.
	- This setup allows for efficient management of video resources and ensures that videos are loaded only when they are about to become visible to the user.
*/

let vimeoScriptLoaded = false
let wistiaScriptLoaded = false

// Initialize Wistia queue immediately
if (typeof _wq === 'undefined') {
    // console.log('🎥 Initializing Wistia queue')
    window._wq = window._wq || []
}

function initVideoEmbeds() {
    // console.log('🎥 Initializing video embeds...')

    // Only load Wistia script if there are Wistia videos on the page
    const wistiaVideos = document.querySelectorAll('[data-wistia-vid-id]')
    if (wistiaVideos.length > 0 && !wistiaScriptLoaded) {
        // console.log('🎥 Loading Wistia script...')
        const wistiaScript = document.createElement('script')
        wistiaScript.setAttribute('async', '')
        wistiaScript.src = 'https://fast.wistia.com/assets/external/E-v1.js'
        wistiaScript.onload = () => {
            // console.log('🎥 Wistia script loaded successfully')
            wistiaScriptLoaded = true
            // Initialize any existing Wistia videos after script loads
            initializeWistiaVideos()
        }
        wistiaScript.onerror = (error) => {
            console.error('🎥 Error loading Wistia script:', error)
        }
        document.head.appendChild(wistiaScript)
    }

    // Initialize video play buttons
    const videoTriggers = document.querySelectorAll('.inline-video-trigger')
    // console.log('🎥 Found video triggers:', videoTriggers.length)

    videoTriggers.forEach((trigger) => {
        // console.log('🎥 Setting up trigger for video:', trigger.dataset.videoId)
        trigger.addEventListener('click', () => {
            // console.log('🎥 Trigger clicked:', trigger.dataset.videoId)
            playVideo(trigger)
        })
    })

    // Initialize lazy loading for videos
    const videoContainers = [
        ...document.querySelectorAll(
            '[data-wistia-vid-id], [data-vimeo-vid-id]'
        ),
    ]
    const observerTriggers = [...document.querySelectorAll('.trigger-observer')]
    // console.log('🎥 Found video containers:', videoContainers.length)
    // console.log('🎥 Found observer triggers:', observerTriggers.length)

    if (observerTriggers.length > 0 && videoContainers.length > 0) {
        observerTriggers.forEach((trigger, index) => {
            const ob = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            // console.log(
                            //     '🎥 Video container intersecting:',
                            //     index
                            // )
                            const videoToLoad = videoContainers[index]
                            if (videoToLoad.dataset.wistiaVidId) {
                                loadWistiaScripts(videoToLoad)
                            } else if (videoToLoad.dataset.vimeoVidId) {
                                loadVimeoScripts(videoToLoad)
                            }
                            ob.unobserve(entry.target)
                            ob.disconnect()
                        }
                    })
                },
                { threshold: 0.1 }
            )
            ob.observe(trigger)
        })
    } else {
        videoContainers.forEach((container) => {
            if (container.dataset.wistiaVidId) {
                loadWistiaScripts(container)
            } else if (container.dataset.vimeoVidId) {
                loadVimeoScripts(container)
            }
        })
    }
}

// Wait for DOM to be ready before initializing
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initVideoEmbeds)
} else {
    initVideoEmbeds()
}

function initializeWistiaVideos() {
    // console.log('🎥 Initializing Wistia videos...')
    const wistiaVideos = document.querySelectorAll('[data-wistia-vid-id]')
    // console.log('🎥 Found Wistia videos to initialize:', wistiaVideos.length)

    wistiaVideos.forEach((video) => {
        const videoId = video.dataset.wistiaVidId
        // console.log('🎥 Initializing Wistia video:', videoId)
        _wq.push({
            id: videoId,
            onReady: function (video) {
                // console.log('🎥 Wistia video ready:', videoId)
            },
            onError: function (error) {
                console.error('🎥 Wistia video error:', error)
            },
        })
    })
}

function playVideo(el) {
    console.log('🎥 Play video triggered:', el.dataset.videoId)

    const videoId = el.dataset.videoId
    if (!videoId) {
        console.warn('🎥 No Video ID Provided')
        return
    }

    // Find the closest parent div with the video container class
    const container = el.closest('[data-wistia-vid-id], [data-vimeo-vid-id]')
    if (!container) {
        console.warn('🎥 No video container found')
        return
    }

    console.log('🎥 Found video container:', container)

    // Get the existing iframe
    const existingIframe = container.querySelector('iframe')
    if (!existingIframe) {
        console.warn('🎥 No iframe found in container')
        return
    }

    let iframeSrc = ''
    if (el.classList.contains('vimeo')) {
        console.log('🎥 Creating Vimeo iframe')
        iframeSrc = `https://player.vimeo.com/video/${videoId}?title=0&byline=0&portrait=0&autoplay=1`

        // Ensure Vimeo script is loaded
        if (!vimeoScriptLoaded) {
            console.log('🎥 Loading Vimeo script on demand')
            const vimeoScript = document.createElement('script')
            vimeoScript.setAttribute('async', '')
            vimeoScript.src = 'https://player.vimeo.com/api/player.js'
            vimeoScript.onload = () => {
                console.log('🎥 Vimeo script loaded successfully')
                vimeoScriptLoaded = true
            }
            vimeoScript.onerror = (error) => {
                console.error('🎥 Error loading Vimeo script:', error)
            }
            document.body.append(vimeoScript)
        } else {
            console.log('🎥 Vimeo script already loaded')
        }
    } else if (el.classList.contains('wistia')) {
        // console.log('🎥 Creating Wistia iframe')
        iframeSrc = `https://fast.wistia.net/embed/iframe/${videoId}?controlsVisibleOnLoad=true&autoplay=1&videoFoam=true`

        // Initialize Wistia video
        // console.log('🎥 Pushing to Wistia queue:', videoId)
        _wq.push({
            id: videoId,
            onReady: function (video) {
                // console.log('🎥 Wistia video ready:', videoId)
                video.play()
            },
            onError: function (error) {
                console.error('🎥 Wistia video error:', error)
            },
        })

        // Ensure Wistia script is loaded
        if (!wistiaScriptLoaded) {
            // console.log('🎥 Loading Wistia script on demand')
            const wistiaScript = document.createElement('script')
            wistiaScript.setAttribute('async', '')
            wistiaScript.src = 'https://fast.wistia.com/assets/external/E-v1.js'
            wistiaScript.onload = () => {
                // console.log('🎥 Wistia script loaded on demand')
                wistiaScriptLoaded = true
            }
            document.body.append(wistiaScript)
        }
    } else {
        // console.log('🎥 Creating YouTube iframe')
        iframeSrc = `https://www.youtube.com/embed/${videoId}?autoplay=1`
    }

    console.log('🎥 Updating iframe src:', iframeSrc)
    console.log('🎥 Iframe element:', existingIframe)
    existingIframe.src = iframeSrc

    // Add a small delay to ensure the iframe loads
    setTimeout(() => {
        console.log('🎥 Iframe src after update:', existingIframe.src)
    }, 100)

    // Add playing class to the container
    container.classList.add('playing')
    console.log('🎥 Video container updated with playing class')

    // Manually hide banner image and play button
    const bannerImage = container.querySelector(
        '[class*="group-[.playing]/video:hidden"]'
    )
    const playButton = container.querySelector('.inline-video-trigger')

    if (bannerImage) {
        bannerImage.style.display = 'none'
        console.log('🎥 Hidden banner image')
    }

    if (playButton) {
        playButton.style.display = 'none'
        console.log('🎥 Hidden play button')
    }
}

// Helper Functions
function loadVimeoScripts(video) {
    if (!video?.dataset?.vimeoVidId) {
        console.warn('🎥 No Vimeo video ID found')
        return
    }

    console.log('🎥 Loading Vimeo video:', video.dataset.vimeoVidId)

    // Check if there's already an iframe with src="about:blank" (for play button functionality)
    const existingIframe = video.querySelector('iframe[src="about:blank"]')

    if (existingIframe) {
        // Update the existing iframe for lazy loading
        const videoId = video.dataset.vimeoVidId
        const settings = video.dataset.vimeoVidSettings || ''
        existingIframe.src = `https://player.vimeo.com/video/${videoId}?${settings}&badge=0&autopause=0&player_id=0&app_id=58479`
        console.log('🎥 Updated existing iframe for lazy loading')
    } else {
        // Create new iframe for containers without existing iframe
        video.insertAdjacentHTML(
            'beforeend',
            `<div style="padding:56.25% 0 0 0;position:relative;">
				<iframe 
					src="https://player.vimeo.com/video/${video.dataset.vimeoVidId}?${video.dataset.vimeoVidSettings || ''}&badge=0&autopause=0&player_id=0&app_id=58479" 
					frameborder="0" 
					allow="autoplay; fullscreen; picture-in-picture; clipboard-write" 
					style="position:absolute;top:0;left:0;width:100%;height:100%;" 
					title="Video Player">
				</iframe>
			</div>`
        )
        console.log('🎥 Created new iframe for lazy loading')
    }

    if (!vimeoScriptLoaded) {
        console.log('🎥 Loading Vimeo script')
        const vimeoScript = document.createElement('script')
        vimeoScript.setAttribute('async', '')
        vimeoScript.src = 'https://player.vimeo.com/api/player.js'
        vimeoScript.onload = () => {
            console.log('🎥 Vimeo script loaded successfully')
            vimeoScriptLoaded = true
        }
        vimeoScript.onerror = (error) => {
            console.error('🎥 Error loading Vimeo script:', error)
        }
        document.body.append(vimeoScript)
        vimeoScriptLoaded = true
    }
}

function loadWistiaScripts(video) {
    if (!video?.dataset?.wistiaVidId) {
        console.warn('🎥 No Wistia video ID found')
        return
    }

    // console.log('🎥 Loading Wistia video:', video.dataset.wistiaVidId)
    if (!wistiaScriptLoaded) {
        // console.log('🎥 Loading Wistia script')
        const wistiaScript = document.createElement('script')
        wistiaScript.setAttribute('async', '')
        wistiaScript.src = 'https://fast.wistia.com/assets/external/E-v1.js'
        wistiaScript.onload = () => {
            wistiaScriptLoaded = true
        }
        wistiaScript.onerror = (error) => {
            console.error('🎥 Error loading Wistia script:', error)
        }
        document.body.append(wistiaScript)
    }
}
